import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { useMyFleet, useQuests, useRecits } from '@/api/hooks'
import { useMercureSubscription, type MercureEventData } from '@/api/mercure'
import { mercureInvalidationKeys } from '@/api/mercureInvalidation'
import { useScrollToTop } from '@/hooks/useScrollToTop'
import { ResourceBanner } from '@/components/ResourceBanner'
import { NotificationBell } from '@/components/NotificationBell'
import { FeedbackButton } from '@/components/FeedbackButton'
import { InstallBanner } from '@/pwa/InstallBanner'
import { InstallAppButton } from '@/pwa/InstallAppButton'
import { PlayLink } from '@/components/PlayLink'
import { QuestNavBadge } from '@/components/QuestNavBadge'
import { RecitNavBadge } from '@/components/RecitNavBadge'
import { SeasonCountdown } from '@/components/SeasonCountdown'
import { StreakChip } from '@/components/StreakChip'
import { PennantIcon, MenuIcon, CloseIcon, BookIcon, ScrollIcon, GrimoireIcon, DiscordIcon, CrownIcon } from '@/components/game/GameIcons'
import { ReputationIcon } from '@/components/game/ReputationIcon'
import { seasonClock } from '@/lib/seasonClock'
import { StickyResourcesContext } from './stickyResources'

// Négociations volontairement absent : on y accède depuis le Tableau de bord
// (carte de réputation) et la page Capitaine — ça libère de la place dans une
// barre déjà serrée. Le récit, lui, vit en emblème icône à côté de la cloche.
const NAV_LINKS = [
  { to: '/', label: 'Tableau de bord' },
  { to: '/island', label: 'Mon île' },
  { to: '/fleet', label: 'Ma flotte' },
  { to: '/map', label: 'Carte' },
  { to: '/taverne', label: 'Taverne' },
  { to: '/forge', label: 'Forge' },
] as const

/**
 * Le Grand Chantier n'entre dans la barre qu'une fois le Quartier-Général au
 * niveau 15, et le libellé est court à dessein : « Grand Chantier » en septième
 * entrée fait déborder la barre à 1280 px et la pastille de ressources passe
 * par-dessus. C'est le seul lien conditionnel de NAV_LINKS.
 *
 * Ce seuil est un filtre d'AFFICHAGE, pas une autorisation : la vérité vit dans
 * EligibilityRules côté serveur, qui exige en plus l'objectif Maître-forgeron.
 * L'écran est donc atteignable par son URL et s'explique lui-même s'il est
 * verrouillé.
 */
const CHANTIER_LINK = { to: '/chantier', label: 'Phare' } as const
const CHANTIER_MIN_HQ_LEVEL = 15

export function AppLayout() {
  const { user, logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const qc = useQueryClient()
  // React Router v7 keeps the scroll position across routes; reset to the top on
  // every pathname change (instantly), except hash navigations (anchor jumps).
  useScrollToTop()
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = () => setMenuOpen(false)
  // Mon île shows the full ResourceBar at the top, so its header mini-banner is
  // suppressed there — UNTIL that full bar scrolls away, at which point the page
  // flips this on (via StickyResourcesContext) so the mini-banner appears flush
  // in the sticky header, just like every other page.
  const [islandResourcesSticky, setIslandResourcesSticky] = useState(false)
  const showMobileResources = pathname !== '/island' || islandResourcesSticky
  // The public landing (guest "/") gets a full-bleed hero with the logo baked
  // into it, so the global header is suppressed there. Every other route —
  // including the logged-in dashboard at "/" — keeps the sticky header.
  const onPublicLanding = !isAuthenticated && pathname === '/'
  // CTA d'inscription vitrine : proposé dans le header aux visiteurs déconnectés
  // sur les pages de contenu public (Codex, Nouveautés, Legal). Pas sur la landing
  // (pas de header là-bas) ni sur /login|/register (redondant).
  const showVitrineCta = !isAuthenticated
    && (pathname === '/codex' || pathname === '/nouveautes' || pathname === '/legal')

  const { data: fleet } = useMyFleet({ enabled: isAuthenticated })
  const { data: quests } = useQuests(!!user)
  const claimable = quests?.completed.length ?? 0
  // Locked only when we KNOW the tavern is not built; unknown/loading → not locked.
  const tavernLocked = quests?.tavern ? !quests.tavern.built : false

  // Niveau de QG lu dans la charge de flotte déjà en main : aucune requête de plus
  // rien que pour décider d'afficher une entrée de menu.
  const hqLevel = (fleet?.buildings ?? []).find((b) => b.type === 'hq')?.level ?? 0
  const navLinks = hqLevel >= CHANTIER_MIN_HQ_LEVEL ? [...NAV_LINKS, CHANTIER_LINK] : NAV_LINKS

  const { data: recits } = useRecits(!!user)
  const recitsList = recits?.recits ?? []
  const hasRecits = recitsList.length > 0
  const recitsActive = recitsList.filter((r) => r.status === 'in_progress').length

  // Pied de page : la saison VIVANTE, lue sur /api/me, donc aucune requête de
  // plus. Le « Saison 1 » codé en dur qui vivait ici aurait menti à la seconde
  // du premier reset, sur toutes les pages du jeu.
  //
  // Trois replis, et chacun correspond à un état réel : un visiteur déconnecté
  // n'a pas de saison (le pied de page vit hors du garde d'authentification) et
  // la mention disparaît plutôt que d'afficher une valeur inventée ; une ligne
  // de saison inexploitable garde le nom et perd le compteur ; sinon on écrit
  // les deux. Le compteur se dérive des dates, jamais de SEASON_LENGTH_WEEKS.
  const footerClock = user?.season ? seasonClock(user.season.startedAt, user.season.endsAt) : null
  const seasonFooter = user?.season
    ? ` · ${user.season.name}${footerClock ? ` · semaine ${footerClock.week} / ${footerClock.totalWeeks}` : ''}`
    : ''

  const onMercure = useCallback(
    (event: MercureEventData) => {
      // Routing lives in a pure, unit-tested helper. Notably `building_upgraded`
      // refreshes quests + objectives too, so finishing the tavern unlocks its
      // nav entry and validates the objective live — no manual reload.
      for (const queryKey of mercureInvalidationKeys(event)) {
        qc.invalidateQueries({ queryKey })
      }
    },
    [qc],
  )
  useMercureSubscription(
    user
      ? [
          `/users/${user.id}/buildings`,
          `/users/${user.id}/missions`,
          `/users/${user.id}/ships`,
          `/users/${user.id}/notifications`,
          `/users/${user.id}/quests`,
          `/users/${user.id}/forge`,
          `/users/${user.id}/chantier`,
        ]
      : [],
    onMercure,
    !!user,
  )

  const handleLogout = () => {
    closeMenu()
    logout()
    navigate('/login')
  }

  return (
    <StickyResourcesContext.Provider value={setIslandResourcesSticky}>
    <div className="min-h-screen flex flex-col text-ocean-50 relative">
      {/* deep-ocean page backdrop */}
      <div className="fixed inset-0 -z-20" aria-hidden="true"
        style={{ background: 'radial-gradient(120% 70% at 50% -8%, #0e5567 0%, #0A3E4F 40%, #082f3a 70%, #04161c 100%)' }} />
      <div className="fixed inset-0 -z-20 opacity-[0.04]" aria-hidden="true"
        style={{ backgroundImage: 'linear-gradient(to right,#5FBDD0 1px,transparent 1px),linear-gradient(to bottom,#5FBDD0 1px,transparent 1px)', backgroundSize: '64px 64px' }} />

      {!onPublicLanding && (
      <header className="sticky top-0 z-30 border-b border-sand-500/15 bg-ocean-900/85 backdrop-blur-md">
        {/* La barre se desserre en DEUX temps, et le plafond du conteneur monte
            avec le contenu qu'il doit porter, jamais avant :
            - 2xl (1536 px) élargit la boîte à 1600 px et rend le nom du
              capitaine ;
            - 3xl (1760 px) rend le reste (plafonds d'entrepôt sur la pilule, mot
              « Déconnexion », gaps desserrés) et pousse la boîte à 1760 px.
            Le découpage n'est pas esthétique, il est arithmétique : la barre
            complète demande 1624 px, quand 2xl n'offre que 1560 px utiles. Tout
            révéler à 2xl, ce qu'on faisait, creusait 64 px de déficit et la nav
            passait par-dessus les ressources. Voir tailwind.config.js. */}
        <div className="max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1760px] mx-auto px-4 sm:px-5 h-16 flex items-center justify-between gap-3">
          {/* xl:shrink-0 est la GARDE, et c'est elle qui rend le chevauchement
              structurellement impossible : la nav est shrink-0 mais sa boîte ne
              l'était pas, donc le flex écrasait la boîte et la nav débordait
              par-dessus la pilule au lieu de se rétrécir. Boîte rigide, toute la
              pression tombe sur le seul élément conçu pour l'absorber, le nom du
              capitaine, qui tronque. Bornée à xl parce qu'en dessous le cluster
              de droite compte sur sa souplesse : figé dès 320 px, il déborderait
              la barre de 94 px. */}
          <div className="flex items-center gap-3 xl:gap-4 xl:shrink-0">
            <Link to="/" onClick={closeMenu} className="font-display text-xl sm:text-2xl text-sand-300 inline-flex items-center gap-2 tracking-wide hover:text-sand-100 transition-colors shrink-0">
              <img src="/corsalia-logo.svg" alt="" className="w-8 h-8 sm:w-9 sm:h-9 -my-1" />
              Corsalia
            </Link>
            {/* Nav inline à partir de xl ; en dessous le burger prend le relais. Avec
                8 entrées (Récits inclus) la barre est serrée à 1280px, d'où la
                compaction xl→2xl à droite : avatar sans nom + ressources sans plafond. */}
            {isAuthenticated && (
              <nav className="hidden xl:flex shrink-0 items-center gap-0.5 text-sm">
                {navLinks.map((l) =>
                  l.to === '/taverne' ? (
                    <span key={l.to} className="relative">
                      {tavernLocked ? (
                        <span
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md whitespace-nowrap opacity-60 cursor-not-allowed select-none"
                          aria-disabled="true"
                          title="Construis ta taverne sur Mon île"
                        >
                          <span className="text-ocean-300">{l.label}</span>
                          <span aria-hidden="true">🔒</span>
                        </span>
                      ) : (
                        <>
                          <NavItem to={l.to}>{l.label}</NavItem>
                          <QuestNavBadge count={claimable} />
                        </>
                      )}
                    </span>
                  ) : (
                    <NavItem key={l.to} to={l.to}>{l.label}</NavItem>
                  ),
                )}
              </nav>
            )}
          </div>

          {/* Le cluster de droite reprend son gap MOBILE de xl à 3xl, la tranche
              la plus serrée de la barre : la nav inline y est déjà complète alors
              que le conteneur, lui, ne grandit pas encore assez. Mesuré à 1280 px
              sur une barre complète (sept entrées de nav, ressources, et sept
              emblèmes dont les Récits) : à 10 px de gap la pilule passait
              par-dessus « Phare » de 9 px, à 6 px il reste 9 px d'écart. C'est
              exactement le débordement que le libellé court « Phare » avait déjà
              servi à éviter une première fois, que le sablier de fin de saison a
              fait revenir, et que le palier 3xl solde pour de bon. */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 xl:gap-1.5 3xl:gap-2.5 min-w-0">
            {showVitrineCta && (
              <PlayLink
                path="/register"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-gradient-to-b from-sand-300 to-sand-500 px-3.5 sm:px-5 py-2 sm:py-2.5 font-display text-sm sm:text-base font-bold text-ocean-900 ring-1 ring-sand-100/60 shadow-md shadow-sand-500/25 transition-transform hover:-translate-y-0.5 active:translate-y-0"
              >
                <span className="grid place-items-center w-5 h-5 rounded-full bg-ocean-900/15" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 translate-x-[0.5px]" fill="currentColor">
                    <path d="M7 4.5v15l13-7.5z" />
                  </svg>
                </span>
                Lève l'ancre !
              </PlayLink>
            )}
            {isAuthenticated && fleet?.resources && (
              <div className="hidden sm:block shrink-0">
                <ResourceBanner resources={fleet.resources} />
              </div>
            )}
            {user && (
              <>
                {/* Classements — persistent gold crown, beside Récits + the bell,
                    at every breakpoint (never collapses into the burger). It is the
                    Classements identity, so it lives here, not in the text nav.
                    xl:shrink-0, comme la lanterne et le sablier qui l'avaient déjà :
                    un bouton carré de 36 px n'est pas une variable d'ajustement, et
                    le flex le rabotait à 29 px sur la tranche serrée. Le nom du
                    capitaine est le seul à céder. */}
                <NavLink
                  to="/classements"
                  onClick={closeMenu}
                  title="Classements"
                  aria-label="Classements"
                  className={({ isActive }) =>
                    `relative grid place-items-center w-9 h-9 xl:shrink-0 rounded-md transition-colors ${isActive ? 'bg-ocean-700/40' : 'hover:bg-ocean-700/40'}`
                  }
                >
                  <CrownIcon className="w-5 h-5 text-sand-300 drop-shadow-[0_0_6px_rgba(229,204,143,0.65)]" />
                </NavLink>
                {/* Sablier de fin de saison : absent les neuf dixièmes du temps,
                    il n'apparaît que dans la dernière quinzaine (fiche 31). C'est
                    le seul préavis de reset du jeu, et la barre reste strictement
                    intacte le reste du temps, ce qui est la seule raison pour
                    laquelle une entrée de plus est tenable ici. Il replie son
                    compte en badge aux deux largeurs serrées, sous sm et entre xl
                    et 2xl, comme la lanterne juste en dessous. */}
                <SeasonCountdown />
                {/* Carte au trésor — lanterne allumée quand la croix du jour est tracée.
                    Compacte (icône + badge nombre) pour tenir dans la barre mobile
                    déjà dense (couronne/récits/cloche/burger). */}
                <StreakChip />
                {/* Récits — icon-only, beside the notification parrot, at every
                    breakpoint (shown only once unlocked). The text entry used to
                    live in the nav; here it's a persistent emblem like the bell. */}
                {hasRecits && (
                  <NavLink
                    to="/recits"
                    onClick={closeMenu}
                    title="Récits"
                    aria-label="Récits"
                    className="relative grid place-items-center w-9 h-9 xl:shrink-0 rounded-md hover:bg-ocean-700/40 transition-colors"
                  >
                    <GrimoireIcon className="w-5 h-5" />
                    <RecitNavBadge count={recitsActive} />
                  </NavLink>
                )}
                <NotificationBell />
                {/* Livret de bord PRIVÉ (/capitaine) — c'est là que vivent la cabine
                    et les réglages ; la vitrine publique reste accessible depuis la
                    page privée (« Voir mon profil public ») et les classements. */}
                <NavLink
                  to="/capitaine"
                  end
                  className={({ isActive }) =>
                    `hidden lg:flex min-w-0 items-center gap-1.5 text-sm px-2 py-1 rounded-md border transition-colors ${
                      isActive
                        ? 'text-sand-200 bg-ocean-700/40 border-sand-500/25'
                        : 'text-ocean-100 border-transparent hover:bg-ocean-700/40 hover:text-sand-200'
                    }`
                  }
                  title="Voir le livret de bord"
                >
                  <span className="grid place-items-center w-7 h-7 shrink-0 rounded-full bg-ocean-900/60 border border-sand-500/30 overflow-hidden">
                    {user.reputation
                      ? <ReputationIcon
                          faction={user.reputation.faction}
                          rank={user.reputation.rank}
                          size={24}
                          title={user.reputation.title}
                        />
                      : <PennantIcon className="w-3.5 h-3.5 text-sand-300" />}
                  </span>
                  {/* Name shows only when there's room (2xl+); below that the chip is a
                      clean emblem-only avatar button — at xl the 8-entry nav + resources
                      already fill the bar, so the name would overflow it.
                      Le pseudo monte à 30 caractères (RegisterUserInput), donc il tronque
                      par conception : c'est lui, et lui seul, qui absorbe le manque de
                      place. Sans la garde xl:shrink-0 posée plus haut, un pseudo long
                      rechevauchait la pilule de 24 px même sur une barre à 1760 px. */}
                  <span className="hidden 2xl:block truncate">Capitaine <strong className="text-sand-300 font-display">{user.pseudo}</strong></span>
                </NavLink>
                {/* Le libellé ne revient qu'à 3xl. De xl à 3xl la barre est pleine
                    (nav inline + ressources, tous deux shrink-0) et c'est le texte
                    le moins utile : l'ancre suffit, avec son title. Il valait 97 px
                    sur les 381 que 2xl révélait d'un coup, pour un conteneur qui
                    n'en gagnait que 320. */}
                <button
                  onClick={handleLogout}
                  title="Déconnexion"
                  aria-label="Déconnexion"
                  className="hidden xl:inline-flex shrink-0 items-center gap-1.5 text-sm px-2.5 3xl:px-3 py-1.5 rounded-md border border-ocean-500/40 text-ocean-100 hover:border-sand-300/50 hover:text-sand-200 transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-4 h-4 shrink-0" aria-hidden="true">
                    <path d="M12 3v9" strokeLinecap="round" />
                    <circle cx="12" cy="15" r="2.5" />
                    <path d="M5.5 15a6.5 6.5 0 0 0 13 0" strokeLinecap="round" />
                    <path d="M9 6h6" strokeLinecap="round" />
                  </svg>
                  <span className="hidden 3xl:inline">Déconnexion</span>
                </button>
                {/* Mobile menu toggle */}
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  className="relative xl:hidden grid place-items-center w-11 h-11 -mr-1.5 rounded-md text-ocean-100 hover:text-sand-200 hover:bg-ocean-700/40 transition-colors"
                  aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                  aria-expanded={menuOpen}
                >
                  {menuOpen ? <CloseIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
                  {/* Macaron: a reward awaits — visible without opening the menu. */}
                  {claimable > 0 && !menuOpen && (
                    <span
                      className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rum-500"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile-only persistent resource bar — always know your treasury from any
            screen (except Mon île, which already shows the full ResourceBar). */}
        {isAuthenticated && fleet?.resources && showMobileResources && (
          <div className="sm:hidden border-t border-sand-500/12 px-3 py-1.5 flex justify-center">
            <ResourceBanner resources={fleet.resources} />
          </div>
        )}

        {/* Mobile menu panel */}
        {isAuthenticated && menuOpen && (
          <div className="xl:hidden border-t border-sand-500/15 bg-ocean-900/95 backdrop-blur-md">
            <nav className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
              {navLinks.map((l) =>
                l.to === '/taverne' && tavernLocked ? (
                  <div
                    key={l.to}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md text-base opacity-60 cursor-not-allowed select-none"
                    aria-disabled="true"
                  >
                    <span className="text-ocean-300">{l.label}</span>
                    <span aria-hidden="true">🔒</span>
                  </div>
                ) : (
                  <MobileNavItem key={l.to} to={l.to} onClick={closeMenu}>
                    {l.label}
                    {/* Menu ouvert, le macaron du burger s'efface : c'est ici que le
                        joueur doit voir laquelle des entrées porte sa récompense. */}
                    {l.to === '/taverne' && <QuestNavBadge count={claimable} inline />}
                  </MobileNavItem>
                ),
              )}
              {user && (
                <MobileNavItem to="/capitaine" onClick={closeMenu}>
                  Capitaine <strong className="text-sand-300 font-display">{user.pseudo}</strong>
                </MobileNavItem>
              )}
              <InstallAppButton variant="menu" onDone={closeMenu} />
              <button
                onClick={handleLogout}
                className="mt-2 text-left px-3 py-2.5 rounded-md border border-ocean-500/40 text-ocean-100 hover:border-sand-300/50 hover:text-sand-200 transition-colors"
              >
                Déconnexion
              </button>
            </nav>
          </div>
        )}
      </header>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-sand-500/12 px-4 py-6 text-xs text-ocean-300">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          {/* Community CTA — recognisable Discord blurple, the most prominent
              footer element so visitors and captains alike are nudged to join. */}
          <a
            href="https://discord.gg/T52KMyghv"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/45 bg-[#5865F2]/15 px-4 py-2 text-sm text-ocean-50 hover:bg-[#5865F2]/25 hover:border-[#5865F2]/70 transition-colors"
          >
            <DiscordIcon className="w-4 h-4 text-[#8b93f8]" />
            Rejoins l'équipage sur Discord
          </a>

          {/* py-1 -my-1 lifts each tap target to ~24px (WCAG 2.5.8) without
              changing the visual rhythm. */}
          <nav className="flex items-center justify-center gap-x-3 gap-y-1 flex-wrap">
            <Link to="/codex" className="inline-flex items-center gap-1.5 py-1 -my-1 hover:text-sand-300 transition-colors">
              <BookIcon className="w-3.5 h-3.5" />
              Codex
            </Link>
            <FooterDot />
            <Link to="/nouveautes" className="inline-flex items-center gap-1.5 py-1 -my-1 hover:text-sand-300 transition-colors">
              <ScrollIcon className="w-3.5 h-3.5" />
              Nouveautés
            </Link>
            <FooterDot />
            <Link to="/legal#mentions" className="py-1 -my-1 hover:text-sand-300 transition-colors">Mentions légales</Link>
            <FooterDot />
            <Link to="/legal#cgu" className="py-1 -my-1 hover:text-sand-300 transition-colors">CGU</Link>
            <FooterDot />
            <Link to="/legal#confidentialite" className="py-1 -my-1 hover:text-sand-300 transition-colors">Confidentialité</Link>
          </nav>

          <p className="text-ocean-300">Corsalia{seasonFooter} · © 2026</p>
        </div>
      </footer>

      {isAuthenticated && <InstallBanner />}
      {/* Feedback trigger — reachable partout (même déconnecté), SAUF la landing
          invitée qu'on garde épurée / centrée sur la conversion. */}
      {!onPublicLanding && <FeedbackButton />}
    </div>
    </StickyResourcesContext.Provider>
  )
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `relative px-2 py-1.5 rounded-md whitespace-nowrap transition-colors ${
          isActive
            ? 'text-sand-300 font-medium after:content-[""] after:absolute after:left-2 after:right-2 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-sand-300'
            : 'text-ocean-100 hover:text-sand-300'
        }`
      }
    >
      {children}
    </NavLink>
  )
}

function FooterDot() {
  return <span aria-hidden="true" className="text-sand-500/40">·</span>
}

function MobileNavItem({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      className={({ isActive }) =>
        `px-3 py-2.5 rounded-md text-base transition-colors ${
          isActive ? 'text-sand-300 font-medium bg-ocean-700/40' : 'text-ocean-100 hover:bg-ocean-700/40 hover:text-sand-300'
        }`
      }
    >
      {children}
    </NavLink>
  )
}
